import { useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

/**
 * ✅ JOTFORM EMBED
 */
const JOTFORM_IFRAME_ID = "JotFormIFrame-260321183127043";
const JOTFORM_SRC = "https://form.jotform.com/260321183127043";
const JOTFORM_EMBED_HANDLER_SCRIPT =
  "https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js";

/**
 * ✅ GOOGLE MAPS EMBED
 */
const GOOGLE_MAPS_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d106432.38478784395!2d-101.9296159!3d33.5778631!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86fe12add37ddd39%3A0x1af0042922e84287!2sLubbock%2C%20TX!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus";

const contactInfo = [
  {
    icon: Phone,
    title: "Phone",
    value: "(806) 555-8123",
    href: "tel:8065558123",
    description: "Call or text anytime",
  },
  {
    icon: Mail,
    title: "Email",
    value: "westtexassparkle@gmail.com",
    href: "mailto:westtexassparkle@gmail.com",
    description: "We'll respond within 24 hours",
  },
  {
    icon: Clock,
    title: "Hours",
    value: "Mon–Sat: 8am–6pm",
    description: "Sunday: Closed",
  },
  {
    icon: MapPin,
    title: "Service Areas",
    value: "Lubbock, Wolfforth, Levelland",
    description: "West Texas",
  },
];

export default function Contact() {
  useEffect(() => {
    // 1) Load Jotform embed handler script once
    const alreadyLoaded = document.querySelector(
      `script[src="${JOTFORM_EMBED_HANDLER_SCRIPT}"]`
    );

    const init = () => {
      // 2) Initialize resize + embed behavior
      if (window.jotformEmbedHandler) {
        window.jotformEmbedHandler(
          `iframe[id='${JOTFORM_IFRAME_ID}']`,
          "https://form.jotform.com/"
        );
      }
    };

    if (alreadyLoaded) {
      init();
      return;
    }

    const script = document.createElement("script");
    script.src = JOTFORM_EMBED_HANDLER_SCRIPT;
    script.async = true;
    script.onload = init;
    document.body.appendChild(script);
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="bg-gradient-to-b from-sky-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Ready for a sparkling clean home or office? Request a free quote or
              give us a call. We&apos;re here to help.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT CONTENT */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* LEFT — JOTFORM */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Request a Free Quote
              </h2>

              <div className="w-full rounded-xl overflow-hidden border border-slate-200 bg-white">
                <iframe
                  id={JOTFORM_IFRAME_ID}
                  title="Request a Free Quote"
                  src={JOTFORM_SRC}
                  style={{
                    width: "100%",
                    height: "900px",
                    border: "none",
                  }}
                  scrolling="no"
                  allow="geolocation; microphone; camera; fullscreen; payment"
                />
              </div>
            </div>

            {/* RIGHT — CONTACT INFO + MAP */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Contact Information
              </h2>

              {/* CLICK TO CALL */}
              <a href="tel:8065558123" className="block mb-8">
                <Card className="bg-sky-600 border-0 hover:bg-sky-700 transition-colors cursor-pointer">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                      <Phone className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-sky-100 text-sm mb-1">Call or Text Now</p>
                      <p className="text-white text-2xl font-bold">(806) 555-8123</p>
                    </div>
                  </CardContent>
                </Card>
              </a>

              {/* INFO CARDS */}
              <div className="space-y-4">
                {contactInfo.slice(1).map((info, index) => (
                  <Card key={index} className="bg-slate-50 border-0">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{info.title}</p>
                        {info.href ? (
                          <a href={info.href} className="text-sky-600 hover:text-sky-700">
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-slate-700">{info.value}</p>
                        )}
                        <p className="text-sm text-slate-500">{info.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* MAP */}
              <div className="mt-8">
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                  Find Us in Lubbock
                </h3>

                <div className="aspect-video rounded-xl overflow-hidden border border-slate-200">
                  <iframe
                    src={GOOGLE_MAPS_EMBED_URL}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="West Texas Sparkle Cleaning - Lubbock, TX"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
